import { createContext, useContext, useState, useEffect } from 'react';

// Theme background images - dramatic creepy space/alien imagery
const themeBackgrounds = {
  original: 'https://images.unsplash.com/photo-1723636963197-98733374c804?crop=entropy&cs=srgb&fm=jpg&q=85', // Deep milky way
  cosmicHorror: 'https://images.unsplash.com/photo-1706800695764-f06756229672?crop=entropy&cs=srgb&fm=jpg&q=85', // Dark rays from black hole
  alienEncounter: 'https://images.unsplash.com/photo-1709404700743-2c14daea63ee?crop=entropy&cs=srgb&fm=jpg&q=85', // Merging stars (UFO-like)
  deepNebula: 'https://images.unsplash.com/photo-1729722615809-45b3f2ad1747?crop=entropy&cs=srgb&fm=jpg&q=85', // Squid nebula - creepy
  ufoHunter: 'https://images.unsplash.com/photo-1693508061202-8264bdf1d774?crop=entropy&cs=srgb&fm=jpg&q=85', // UFO over trees
  voidWalker: 'https://images.unsplash.com/photo-1712509846559-8c958d89a0bb?crop=entropy&cs=srgb&fm=jpg&q=85' // Red planetary nebula - hellish
};

// Theme definitions with creepy space/alien aesthetics
export const themes = {
  // Original Skybot theme
  original: {
    id: 'original',
    name: 'Skybot',
    description: 'The original void black with neon cyan',
    background: themeBackgrounds.original,
    fonts: {
      heading: "'Unbounded', sans-serif",
      body: "'Exo 2', sans-serif",
      mono: "'JetBrains Mono', monospace"
    },
    colors: {
      void: '#02040a',
      surface: '#0a0f1e',
      surfaceHighlight: '#151b2e',
      primary: '#00f2ea',
      secondary: '#7d00ff',
      accent: '#ffd700',
      danger: '#ff2e2e',
      text: '#e2e8f0',
      textMuted: '#64748b',
      border: 'rgba(255, 255, 255, 0.1)',
      borderActive: 'rgba(0, 242, 234, 0.5)',
      glass: 'rgba(2, 4, 10, 0.7)',
      gradientStart: '#02040a',
      gradientEnd: '#050a1f'
    },
    effects: {
      glow: '0 0 20px rgba(0, 242, 234, 0.3)',
      textGlow: '0 0 10px rgba(0, 242, 234, 0.5)'
    }
  },

  // Cosmic Horror - Lovecraftian deep space
  cosmicHorror: {
    id: 'cosmicHorror',
    name: 'Cosmic Horror',
    description: 'Eldritch darkness from beyond the stars',
    background: themeBackgrounds.cosmicHorror,
    fonts: {
      heading: "'Creepster', 'Unbounded', cursive",
      body: "'Special Elite', 'Exo 2', serif",
      mono: "'VT323', 'JetBrains Mono', monospace"
    },
    colors: {
      void: '#0a0008',
      surface: '#1a0a18',
      surfaceHighlight: '#2a1028',
      primary: '#8b00ff',
      secondary: '#ff0066',
      accent: '#00ff88',
      danger: '#ff3333',
      text: '#d4c4e8',
      textMuted: '#7a5a8a',
      border: 'rgba(139, 0, 255, 0.2)',
      borderActive: 'rgba(139, 0, 255, 0.6)',
      glass: 'rgba(10, 0, 8, 0.85)',
      gradientStart: '#0a0008',
      gradientEnd: '#1a0520'
    },
    effects: {
      glow: '0 0 30px rgba(139, 0, 255, 0.4), 0 0 60px rgba(255, 0, 102, 0.2)',
      textGlow: '0 0 15px rgba(139, 0, 255, 0.6)'
    }
  },

  // Alien Encounter - Green/bio-luminescent
  alienEncounter: {
    id: 'alienEncounter',
    name: 'Alien Encounter',
    description: 'Bio-luminescent extraterrestrial signals',
    background: themeBackgrounds.alienEncounter,
    fonts: {
      heading: "'Orbitron', 'Unbounded', sans-serif",
      body: "'Rajdhani', 'Exo 2', sans-serif",
      mono: "'Share Tech Mono', 'JetBrains Mono', monospace"
    },
    colors: {
      void: '#000a05',
      surface: '#0a1a10',
      surfaceHighlight: '#152a1c',
      primary: '#00ff66',
      secondary: '#66ff00',
      accent: '#ffff00',
      danger: '#ff4400',
      text: '#c8ffc8',
      textMuted: '#5a8a5a',
      border: 'rgba(0, 255, 102, 0.2)',
      borderActive: 'rgba(0, 255, 102, 0.6)',
      glass: 'rgba(0, 10, 5, 0.85)',
      gradientStart: '#000a05',
      gradientEnd: '#0a2010'
    },
    effects: {
      glow: '0 0 25px rgba(0, 255, 102, 0.4), 0 0 50px rgba(102, 255, 0, 0.2)',
      textGlow: '0 0 12px rgba(0, 255, 102, 0.7)'
    }
  },

  // Deep Nebula - Purple/pink cosmic clouds
  deepNebula: {
    id: 'deepNebula',
    name: 'Deep Nebula',
    description: 'Swirling cosmic clouds and stardust',
    background: themeBackgrounds.deepNebula,
    fonts: {
      heading: "'Audiowide', 'Unbounded', sans-serif",
      body: "'Electrolize', 'Exo 2', sans-serif",
      mono: "'Fira Code', 'JetBrains Mono', monospace"
    },
    colors: {
      void: '#05000a',
      surface: '#150a20',
      surfaceHighlight: '#251535',
      primary: '#ff66cc',
      secondary: '#6666ff',
      accent: '#ffcc00',
      danger: '#ff4466',
      text: '#f0d0ff',
      textMuted: '#8a5a9a',
      border: 'rgba(255, 102, 204, 0.2)',
      borderActive: 'rgba(255, 102, 204, 0.6)',
      glass: 'rgba(5, 0, 10, 0.85)',
      gradientStart: '#05000a',
      gradientEnd: '#200a30'
    },
    effects: {
      glow: '0 0 25px rgba(255, 102, 204, 0.4), 0 0 50px rgba(102, 102, 255, 0.2)',
      textGlow: '0 0 12px rgba(255, 102, 204, 0.6)'
    }
  },

  // UFO Hunter - Military/radar green
  ufoHunter: {
    id: 'ufoHunter',
    name: 'UFO Hunter',
    description: 'Military-grade anomaly detection interface',
    background: themeBackgrounds.ufoHunter,
    fonts: {
      heading: "'Black Ops One', 'Unbounded', sans-serif",
      body: "'Saira Condensed', 'Exo 2', sans-serif",
      mono: "'Source Code Pro', 'JetBrains Mono', monospace"
    },
    colors: {
      void: '#000800',
      surface: '#0a1508',
      surfaceHighlight: '#152510',
      primary: '#39ff14',
      secondary: '#00ff00',
      accent: '#ff6600',
      danger: '#ff0000',
      text: '#c0ffc0',
      textMuted: '#506050',
      border: 'rgba(57, 255, 20, 0.25)',
      borderActive: 'rgba(57, 255, 20, 0.7)',
      glass: 'rgba(0, 8, 0, 0.9)',
      gradientStart: '#000800',
      gradientEnd: '#081808'
    },
    effects: {
      glow: '0 0 20px rgba(57, 255, 20, 0.5), 0 0 40px rgba(0, 255, 0, 0.2)',
      textGlow: '0 0 8px rgba(57, 255, 20, 0.8)'
    }
  },

  // Void Walker - Pure darkness with red accents
  voidWalker: {
    id: 'voidWalker',
    name: 'Void Walker',
    description: 'Darkness that stares back at you',
    background: themeBackgrounds.voidWalker,
    fonts: {
      heading: "'Nosifer', 'Creepster', cursive",
      body: "'Crimson Text', 'Exo 2', serif",
      mono: "'Courier Prime', 'JetBrains Mono', monospace"
    },
    colors: {
      void: '#050000',
      surface: '#100505',
      surfaceHighlight: '#200a0a',
      primary: '#ff0033',
      secondary: '#990000',
      accent: '#ff9900',
      danger: '#ff0000',
      text: '#ffcccc',
      textMuted: '#8a5050',
      border: 'rgba(255, 0, 51, 0.2)',
      borderActive: 'rgba(255, 0, 51, 0.6)',
      glass: 'rgba(5, 0, 0, 0.9)',
      gradientStart: '#050000',
      gradientEnd: '#150505'
    },
    effects: {
      glow: '0 0 30px rgba(255, 0, 51, 0.5), 0 0 60px rgba(153, 0, 0, 0.3)',
      textGlow: '0 0 15px rgba(255, 0, 51, 0.7)'
    }
  }
};

// Google Fonts URL for all theme fonts
export const THEME_FONTS_URL = "https://fonts.googleapis.com/css2?family=Audiowide&family=Black+Ops+One&family=Courier+Prime&family=Creepster&family=Crimson+Text:wght@400;600&family=Electrolize&family=Exo+2:wght@300;400;600&family=Fira+Code:wght@400;700&family=JetBrains+Mono:wght@400;700&family=Nosifer&family=Orbitron:wght@400;700&family=Rajdhani:wght@400;600;700&family=Saira+Condensed:wght@400;600;700&family=Share+Tech+Mono&family=Source+Code+Pro:wght@400;700&family=Special+Elite&family=Unbounded:wght@300;400;600;800&family=VT323&display=swap";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('aether_theme');
    return saved && themes[saved] ? saved : 'original';
  });

  const theme = themes[currentTheme];

  // Apply theme CSS variables to document
  useEffect(() => {
    const root = document.documentElement;
    const t = themes[currentTheme];
    
    // Set CSS custom properties
    root.style.setProperty('--theme-void', t.colors.void);
    root.style.setProperty('--theme-surface', t.colors.surface);
    root.style.setProperty('--theme-surface-highlight', t.colors.surfaceHighlight);
    root.style.setProperty('--theme-primary', t.colors.primary);
    root.style.setProperty('--theme-secondary', t.colors.secondary);
    root.style.setProperty('--theme-accent', t.colors.accent);
    root.style.setProperty('--theme-danger', t.colors.danger);
    root.style.setProperty('--theme-text', t.colors.text);
    root.style.setProperty('--theme-text-muted', t.colors.textMuted);
    root.style.setProperty('--theme-border', t.colors.border);
    root.style.setProperty('--theme-border-active', t.colors.borderActive);
    root.style.setProperty('--theme-glass', t.colors.glass);
    root.style.setProperty('--theme-gradient-start', t.colors.gradientStart);
    root.style.setProperty('--theme-gradient-end', t.colors.gradientEnd);
    root.style.setProperty('--theme-glow', t.effects.glow);
    root.style.setProperty('--theme-text-glow', t.effects.textGlow);
    root.style.setProperty('--theme-font-heading', t.fonts.heading);
    root.style.setProperty('--theme-font-body', t.fonts.body);
    root.style.setProperty('--theme-font-mono', t.fonts.mono);
    
    // Update body background
    document.body.style.background = t.colors.void;
    document.body.style.color = t.colors.text;
    document.body.style.fontFamily = t.fonts.body;
    
    // Save to localStorage
    localStorage.setItem('aether_theme', currentTheme);
  }, [currentTheme]);

  const changeTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      currentTheme, 
      changeTheme, 
      themes,
      themeList: Object.values(themes)
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
